import React, { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../context/OnboardingContext';
import { getFlowForSession } from '../../flows';

// Public steps
import Step1Intent from './steps/Step1Intent';
import AuthGate from './steps/AuthGate';

// Private steps - Common
import Step2Workspace from './steps/Step2Workspace';
import Step3Project from './steps/Step3Project';
import Step4TestSource from './steps/Step4TestSource';
import Step5TestCreationMode from './steps/Step5TestCreationMode';

// AI Path (6A, 7A)
import Step6AAIPreview from './steps/Step6AAIPreview';
import Step7AAutomationPrefs from './steps/Step7AAutomationPrefs';
import Step7AGenerate from './steps/Step7AGenerate';

// Recorder Path (6B, 8B, 10B) - Live recording step removed from main flow
import Step6BInstallAgentExtension from './steps/recorder/Step6BInstallAgentExtension';
import Step8BRecordingSetup from './steps/recorder/Step8BRecordingSetup';
import Step10BReviewRecording from './steps/recorder/Step10BReviewRecording';

// Discovery Path (6C, 7C, 10C)
import Step6CDiscoverySetup from './steps/discovery/Step6CDiscoverySetup';
import Step7CDiscoveryCrawl from './steps/discovery/Step7CDiscoveryCrawl';
import Step10CReviewDiscovery from './steps/discovery/Step10CReviewDiscovery';

// Convergence steps
import Step8RunTest from './steps/Step8RunTest';
import Step9Summary from './steps/Step9Summary';
import { APPLICATION_TYPES, TEST_CREATION_MODES } from '../../Config/Onboarding/constant';

const URL_SYNC_DELAY_MS = 100;

const OnboardingWizard = () => {
  const context = useOnboarding();
  const { currentStep, testCreationMode, project, isComplete } = context;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentStepRef = useRef(currentStep);

  currentStepRef.current = currentStep;

  const mode = searchParams.get('mode');

  // Page content follows engine state — editing the URL cannot change the step.
  // Debounce URL correction so Back/Continue handlers can update currentStep first.
  useEffect(() => {
    if (isComplete) return;

    const timer = setTimeout(() => {
      const urlStep = Number(new URLSearchParams(window.location.search).get('step'));
      const engineStep = currentStepRef.current;

      if (!engineStep || urlStep === engineStep) return;

      const modeParam = new URLSearchParams(window.location.search).get('mode');
      const modeQuery = modeParam ? `&mode=${modeParam}` : '';
      navigate(`/app/onboarding?step=${engineStep}${modeQuery}`, { replace: true });
    }, URL_SYNC_DELAY_MS);

    return () => clearTimeout(timer);
  }, [searchParams, currentStep, isComplete, navigate]);

  // If onboarding is complete, redirect to dashboard
  if (isComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  const step = currentStep;

  // Get the appropriate component based on step and mode
  const getStepComponent = () => {
    const stepNum = parseInt(step);
     console.log('RENDER', { stepNum, currentStep, applicationType: project?.applicationType, intent: context.intent });
    
    // DUAL-MODE ROUTING: Check if using flow registry (IoT/MIGRATE/Future flows)
    // If yes, use flow registry; otherwise use legacy hardcoded routing
    const flow = getFlowForSession(context);
    
    if (flow && (flow.flowId === 'iot_hardware' || flow.flowId === 'migrate_scripts')) {
    const component = flow.getStepComponent(stepNum, context);
    
    if (component) {
      return React.createElement(component);
    }
    
    // Shared steps (1-4) are not in flow registry, fall through to legacy routing below
    if (stepNum >= 1 && stepNum <= 4) {
      // do nothing, fall through
    } else {
      return <Navigate to="/app/onboarding?step=1" replace />;
    }
  }
    
    // LEGACY ROUTING: API/WEB/MOBILE/Manual/JIRA flows use existing hardcoded logic
    
    // Steps 1-5 are common to all paths
    if (stepNum === 1) return <Step1Intent />;
    if (stepNum === 2) return <Step2Workspace />;
    if (stepNum === 3) return <Step3Project />;
    if (stepNum === 4) return <Step4TestSource />;
    if (stepNum === 5) return <Step5TestCreationMode />;

    // Step 6+ branches based on mode or testCreationMode
    const effectiveMode = mode || testCreationMode;

    // Step 6
    if (stepNum === 6) {
      if (effectiveMode === TEST_CREATION_MODES.AI || effectiveMode === 'ai' || project.applicationType === APPLICATION_TYPES.API) {
        return <Step6AAIPreview />;
      } else if (effectiveMode === TEST_CREATION_MODES.RECORDER || effectiveMode === 'recorder') {
        return <Step6BInstallAgentExtension />; // Combined Agent + Extension
      } else if (effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY) {
        // Auto Discover UI → AI Preview, then Discovery Setup at step 7
        return <Step6AAIPreview />;
      } else if (effectiveMode === 'discovery') {
        // Legacy / explicit discovery-setup entry (e.g. from AI Preview warning)
        return <Step6CDiscoverySetup />;
      }
      return <Step5TestCreationMode />; // Redirect back if mode not set
    }

    // Step 7
    if (stepNum === 7) {
      if (effectiveMode === TEST_CREATION_MODES.AI || effectiveMode === 'ai') {
        return <Step7AGenerate />;
      } else if (effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY) {
        // UI Discovery: Setup Tools (agent + extension) sits between AI Preview and Discovery Setup
        return <Step6BInstallAgentExtension />;
      } else if (effectiveMode === 'discovery') {
        return <Step7CDiscoveryCrawl />;
      }
      // Recorder mode: Step 7 is now Step 8 (Recording Setup was merged)
      if (effectiveMode === TEST_CREATION_MODES.RECORDER || effectiveMode === 'recorder') {
        return <Step8BRecordingSetup />;
      }
    }

    // Step 8
    if (stepNum === 8) {
      if (effectiveMode === TEST_CREATION_MODES.RECORDER || effectiveMode === 'recorder') {
        return <Step10BReviewRecording />; // Review recording (live recording step removed)
      }
      if (effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY) {
        return <Step6CDiscoverySetup />;
      }
      // Convergence point - Run Test (AI path / legacy discovery after review)
      return <Step8RunTest />;
    }

    // Step 9
    if (stepNum === 9) {
      if (effectiveMode === TEST_CREATION_MODES.RECORDER || effectiveMode === 'recorder') {
        return <Step8RunTest />; // Execute recorded tests
      }
      if (effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY) {
        return <Step7CDiscoveryCrawl />;
      }
      // Convergence point - Summary
      return <Step9Summary />;
    }

    // Step 10 - Review (UI Discovery / legacy discovery) / Summary (recorder)
    if (stepNum === 10) {
      if (
        effectiveMode === 'ui' ||
        effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY ||
        effectiveMode === 'discovery'
      ) {
        return <Step10CReviewDiscovery />;
      }
      if (effectiveMode === TEST_CREATION_MODES.RECORDER || effectiveMode === 'recorder') {
        return <Step9Summary />;
      }
      return <Step8RunTest />;
    }

    // Step 11 - Execute Test (UI Discovery) / Summary (IoT)
    if (stepNum === 11) {
      if (effectiveMode === 'ui' || effectiveMode === TEST_CREATION_MODES.UI_DISCOVERY) {
        return <Step8RunTest />;
      }
      return <Step9Summary />;
    }

    // Step 12 - Summary (UI Discovery)
    if (stepNum === 12) {
      return <Step9Summary />;
    }

    // Default fallback
    return <Navigate to="/get-started" replace />;
  };

  // Just return the appropriate component directly
  return getStepComponent();
};

export default OnboardingWizard;